/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Dispatch, SetStateAction } from 'react';
import type UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import type { Order } from '@adyen/adyen-web/dist/types/types';
import type { FetchResult } from '@apollo/client';
import type { PlaceOrderMutation } from '../../../graphqlDocument/order/placeOrder.generated';
import { colours } from '../../../stylings';
import type { FormatAddress } from '../utils/formatAddress';

// Adyen docs - web components - gift cards
// https://docs.adyen.com/payment-methods/gift-cards/web-component

export interface CreateGiftCardConfigProps {
  isGiftCardEnough: boolean;
  isGiftCardRemoved: boolean;
  setIsPaymentBtnDisabled: Dispatch<SetStateAction<boolean>>;
  setPaymentInProgress: Dispatch<SetStateAction<boolean>>;
  setPaymentError: Dispatch<SetStateAction<boolean>>;
  setIsGiftCardEnough: Dispatch<SetStateAction<boolean>>;
  setIsErrorGiftCardInput: Dispatch<SetStateAction<boolean>>;
  setCanPayWithGiftCards: Dispatch<SetStateAction<boolean>>;
  setOnErrorGiftCard: Dispatch<SetStateAction<boolean>>;
  placeOrderMutation: () => Promise<FetchResult<PlaceOrderMutation, Record<string, unknown>, Record<string, unknown>>>;
  setGiftCardOrder: (value: SetStateAction<Order | null>) => void;
  getCartAddresses: () => Promise<{
    cartBillingAddress: FormatAddress | null;
    cartDeliveryAddress: FormatAddress | null;
  }>;
}

export const createGiftCardConfig = ({
  isGiftCardEnough,
  isGiftCardRemoved,
  setIsPaymentBtnDisabled,
  setPaymentInProgress,
  setPaymentError,
  setIsGiftCardEnough,
  setIsErrorGiftCardInput,
  setCanPayWithGiftCards,
  setOnErrorGiftCard,
  placeOrderMutation,
  setGiftCardOrder,
  getCartAddresses,
}: CreateGiftCardConfigProps) => ({
  styles: {
    placeholder: {
      color: colours.DIM_GREY,
    },
  },
  beforeSubmit: async (data: any, _: UIElement<any>, actions: any) => {
    setIsPaymentBtnDisabled(true);
    setPaymentInProgress(true);

    const result = await placeOrderMutation();

    if (result.data?.placeOrder.accepted) {
      const { cartBillingAddress, cartDeliveryAddress } = await getCartAddresses();

      actions.resolve({
        ...data,
        ...(cartBillingAddress && { billingAddress: cartBillingAddress }),
        ...(cartDeliveryAddress && { deliveryAddress: cartDeliveryAddress }),
      });
    } else {
      actions.reject();

      setPaymentError(true);
      setIsPaymentBtnDisabled(false);
      setPaymentInProgress(false);
    }
  },
  // Called when the gift card balance is less than the transaction amount
  // https://docs.adyen.com/payment-methods/gift-cards/web-component/?tab=config-sessions_2#required-configuration-1
  onOrderCreated(orderStatus: Order) {
    setPaymentInProgress(false);
    setGiftCardOrder(orderStatus);
  },
  // Called when the gift card balance is enough to pay the full payment amount
  // https://docs.adyen.com/payment-methods/gift-cards/web-component/?tab=config-sessions_2#required-configuration-1
  onRequiringConfirmation() {
    setCanPayWithGiftCards(true);
    setIsGiftCardEnough(true);
  },
  onError: (error: any, component: UIElement) => {
    console.error('GiftCard', error.name, error.message, error.stack, component);

    if (!error.message.toLowerCase().includes('secured field iframes')) {
      setOnErrorGiftCard(true);

      if (!isGiftCardEnough || !isGiftCardRemoved) setIsErrorGiftCardInput(true);
    }
  },
});
